import { useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import Layout from '../layout';
import { useParams, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import api from '../../../api/axiosInstance';
import { Loader } from '../../../components/loader';
import ListItems from '../../../components/list';
import { ChartBarTag } from '../../../components/chart/ChartBarTag';
import PeriodToggle, { PeriodType } from '../../../components/periodToggle';

export type Period = 'years' | 'year' | 'month' | 'week';
type ListType = 'income' | 'expense' | 'all' | 'category';

const LIMIT = 5;

export default function OperationsTag() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');

  const [period, setPeriod] = useState<PeriodType>('year');

  const [customRange, setCustomRange] = useState({
    from: '',
    to: '',
  });

  const listType: ListType = type === 'income' || type === 'expense' ? type : 'all';

  const {
    data: tag,
    isLoading: loadingTag,
    isError: isTagError,
  } = useQuery({
    queryKey: ['tag', id, type],
    queryFn: async () => {
      if (!id || !type) throw new Error('Неверный параметр');

      const endpoint =
        type === 'income' ? `/tag-income/${id}` : type === 'expense' ? `/tag-expense/${id}` : null;

      if (!endpoint) throw new Error('Некорректный тип');

      const res = await api.get(endpoint);
      return res.data;
    },
    enabled: !!id && !!type,
  });

  const {
    data: operationsPages,
    isLoading: loadingOps,
    isError: opsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['operationsByTag', type, id, 'infinite'],
    enabled: !!id && !!type,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      if (!id || !type) return [];

      const endpoint =
        type === 'income'
          ? `/incomes/by-tag?tagId=${id}&limit=${LIMIT}&offset=${pageParam}`
          : `/expenses/by-tag?tagId=${id}&limit=${LIMIT}&offset=${pageParam}`;

      const res = await api.get(endpoint);
      return res.data as any[];
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!Array.isArray(lastPage) || lastPage.length < LIMIT) return undefined;
      return allPages.length * LIMIT;
    },
  });

  const operations = useMemo(
    () => (operationsPages?.pages ? operationsPages.pages.flat() : []),
    [operationsPages],
  );

  const handlePeriodChange = (newPeriod: PeriodType | null) => {
    if (newPeriod) setPeriod(newPeriod);
  };

  return (
    <Layout>
      <h1 style={{ fontSize: '1rem', marginBottom: '1rem', color: '#666' }}>
        {loadingTag ? (
          <Loader />
        ) : isTagError ? (
          'Ошибка загрузки тега'
        ) : tag ? (
          'Тег: ' + tag.name
        ) : (
          'Тег не найден'
        )}
      </h1>

      <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <PeriodToggle
          value={period}
          onChange={handlePeriodChange}
          range={customRange}
          onRangeChange={setCustomRange}
          sx={{ ml: 2, mb: 2 }}
        />
      </Box>
      {type && id ? (
        <ChartBarTag
          period={period}
          type={type as 'income' | 'expense'}
          tagId={Number(id)}
          title={tag?.name}
          from={customRange.from}
          to={customRange.to}
        />
      ) : null}
      <Box sx={{ mt: 6, width: '100%' }}>
        {loadingOps ? (
          <Loader />
        ) : opsError ? (
          <div>Ошибка загрузки операций</div>
        ) : (
          <>
            <ListItems type={listType} items={operations || []} />

            {hasNextPage && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  variant="outlined"
                >
                  {isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Layout>
  );
}
